import { Typography } from '@mui/material';
import { ThemePalette } from 'mui.theme';
import React from 'react';
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Sector,
  Tooltip,
} from 'recharts';
import { generateRandomColor, pastelColorMap } from 'utils/helpers/general';
import { handleFocusRemoveSelectOutline } from '../../Assets/Helper/pieChart.Helper';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const renderActiveShape = (props: any) => {
  const RADIAN = Math.PI / 180;
  const {
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    percent,
    value,
    name,
  } = props;
  const sin = Math.sin(-RADIAN * midAngle);
  const cos = Math.cos(-RADIAN * midAngle);
  const sx = Number(cx) + (Number(outerRadius) + 10) * cos;
  const sy = Number(cy) + (Number(outerRadius) + 10) * sin;
  const mx = Number(cx) + (Number(outerRadius) + 30) * cos;
  const my = Number(cy) + (Number(outerRadius) + 30) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 22;
  const ey = my;
  const textAnchor = cos >= 0 ? 'start' : 'end';
  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <Sector
        cx={cx}
        cy={cy}
        startAngle={startAngle}
        endAngle={endAngle}
        innerRadius={Number(outerRadius) + 6}
        outerRadius={Number(outerRadius) + 10}
        fill={fill}
      />
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        stroke={fill}
        fill="none"
      />
      <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
      <text
        x={Number(ex) + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        dy={-25}
        textAnchor={textAnchor}
        fill={fill}
        fontSize={12}
      >{`${name}`}</text>
      <text
        x={Number(ex) + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        dy={-5}
        textAnchor={textAnchor}
        fill={fill}
        fontSize={10}
      >{`Value: ${(+value).toFixed(2)}`}</text>
      <text
        x={Number(ex) + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        dy={15}
        textAnchor={textAnchor}
        fill="#999"
        fontSize={10}
      >{`Rate: ${(percent * 100).toFixed(0)}%`}</text>
    </g>
  );
};

const CustomTooltip: React.FC<{
  active?: boolean;
  payload?: any;
  label?: string;
  enableDarkTheme: boolean;
  valueLabel?: string;
  customLabel?: string;
}> = ({
  active,
  payload,
  label = 'Asset Name',
  customLabel = 'Asset Name',
  valueLabel = 'Total Asset Cost',
  enableDarkTheme = false,
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  if (active && payload && payload.length) {
    const hoverCell = payload[0].payload;

    return (
      <div
        className="custom-tooltip"
        style={{
          backgroundColor: enableDarkTheme
            ? palette.pageBackground
            : palette.pageBackground,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: palette.textPrimary,
          }}
        >
          {customLabel || label}: {hoverCell.name}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: palette.textPrimary,
          }}
        >
          {valueLabel}: {(+hoverCell.total_asset_cost)?.toFixed(2)}
        </Typography>
      </div>
    );
  }

  return null;
};

export const ComponentsPieChart = ({
  data,
  handlePieClick,
  enableDarkTheme = false,
  onlyOneActive = false,
  label,
  valueLabel,
  dataKey = 'total_asset_cost',
  activeIndices,
}: {
  data: Array<Record<string, any>>;
  handlePieClick: any;
  enableDarkTheme: boolean;
  onlyOneActive?: boolean;
  label?: string;
  valueLabel?: string;
  dataKey?: string;
  activeIndices?: number[];
}) => {
  const onPieClick = (data: any, index: number) => {
    handlePieClick(data, index);
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <div tabIndex={0} onFocus={handleFocusRemoveSelectOutline}>
        <PieChart width={500} height={400}>
          <Pie
            dataKey="total_asset_cost"
            isAnimationActive={true}
            data={data}
            cx="45%"
            cy="40%"
            innerRadius={80}
            outerRadius={120}
            fill="#8884d8"
            onClick={onPieClick}
            labelLine={false}
            activeIndex={activeIndices}
            activeShape={renderActiveShape}
          >
            {data.map((entry: any, index: number) => (
              <Cell key={`cell-${index}`} fill={getColourByIndex(index)} />
            ))}
          </Pie>
          {/* <Legend /> */}
          <Tooltip
            content={
              <CustomTooltip
                enableDarkTheme={enableDarkTheme}
                customLabel={label}
                valueLabel={valueLabel}
              />
            }
          />
        </PieChart>
      </div>
    </ResponsiveContainer>
  );
};

function getColourByIndex(index: number) {
  return pastelColorMap[index] || generateRandomColor();
}

const isEqual = (
  arr1: Array<Record<string, any>>,
  arr2: Array<Record<string, any>>
): boolean => {
  if (arr1.length !== arr2.length) return false;
  for (let i = 0; i < arr1.length; i++) {
    if (JSON.stringify(arr1[i]) !== JSON.stringify(arr2[i])) {
      return false;
    }
  }
  return true;
};
